import { _ } from "../../../core/polyglot/polyglot.js";
import CssClassNameVisitor from "../../../models/reports/reports/CssClassNameVisitor.js";
import ReportVisitor from "../../../models/reports/reports/ReportVisitor.js";
import RowTitleRenderer from "../RowTitleRenderer/RowTitleRenderer.js";

/**
 * This visitor is responsible for rendering the {@link AbstractRow}
 * rows of a report as <tr> DOM nodes. A report row usually triggers
 * the creation of a single <tr> but a group row will be mapped to
 * several consecutive <tr> nodes.
 *
 * @param {Report} report - The report containing the rows being
 *   rendered
 * @param {AbstractColumn[]} columns - The report columns that are
 *   rendered. Each column will map to a different <td>
 * @param {Renderer} dataCellRenderer - This function is called with a
 *  row, a column, a CSS class and a content and is expected to render
 *  a <td> with the specified content
 * @param {Renderer} [itemTitleRenderer] - Takes an {@link
 *   AbstractRow} as argument and should return a short representation
 *   of this item. The default is to use the return value of calling
 *   `getItem()` on the argument
 * @param {Renderer} [dimensionsRenderer] - Takes an {@link AbstractItem}
 *   as argument and should return a renderable for its dimensions
 * @param {Renderer} [rowTitleRenderer] - Takes an {@link AbstractRow}
 *   as argument and should return a <th> element with a short
 *   description of the row. Use {@link RowTitleRenderer} by default
 * @param {boolean} [isFlat] - Should be false (the default) if the
 *   receiver should display an expand/collapse arrow next to rows
 *   with child rows
 * @param {function} onGroupRowCollapseExpandRequest - This function
 *   is called with a {@link GroupRow} as argument when the user
 *   clicks on the expand/collapse icon
 */
export default class RowRenderer extends ReportVisitor {
	constructor({
		report,
		columns,
		dataCellRenderer,
		itemTitleRenderer,
		dimensionsRenderer,
		rowTitleRenderer,
		isFlat,
		onGroupRowCollapseExpandRequest,
	}) {
		super(...arguments);

		this._report = report;
		this._columns = columns;
		this._dataCellRenderer = dataCellRenderer;
		this._cssClassNameRenderer = new CssClassNameVisitor();
		this._report = report;

		this._rowTitleRenderer =
			rowTitleRenderer ||
			this._makeDefaultRowTitleRenderer({
				report,
				itemTitleRenderer,
				dimensionsRenderer,
				isFlat,
				onGroupRowCollapseExpandRequest,
			});
	}

	visitAbstractRow(row) {
		return (html) =>
			html.tr(
				{ klass: this._rowCssClasses(row) },
				this._rowTitleRenderer(row),
				this._dataCellsRenderer({ row }),
			);
	}

	visitAccountRow(accountRow) {
		return (html) => {
			html.tr(
				{
					klass: this._rowCssClasses(accountRow),
					"data-test": "account-row",
				},
				this._rowTitleRenderer(accountRow),
				this._dataCellsRenderer({ row: accountRow }),
			);
		};
	}

	visitGroupRow(groupRow) {
		return groupRow.isCollapsed()
			? this._collapsedGroupRowRenderer(groupRow)
			: this._expandedGroupRowRenderer(groupRow);
	}

	visitRepeatingRow(repeatingRow) {
		return this.visitAll(repeatingRow.getVisibleRows());
	}

	visitSpacerRow(row) {
		return (html) => {
			html.tr(
				{ klass: this._rowCssClasses(row) },
				html.th((html) => html.raw("&nbsp;")),
				this._emptyDataColumnsRenderer({ row }),
			);
		};
	}

	visitStaticReportRow(row) {
		return (html) => {
			html.tr(
				{ class: row.getStyle() },
				this._rowTitleRenderer(row),
				this._dataCellsRenderer({ row }),
			);
		};
	}

	_collapsedGroupRowRenderer(group) {
		return (html) => {
			html.tr(
				{ klass: ["collapsed", this._rowCssClasses(group)] },
				this._rowTitleRenderer(group),
				this._dataCellsRenderer({ row: group }),
			);

			const visibleLeafRows = this._getGroupRowVisibleLeaves(group);

			html.render(this.visitAll(visibleLeafRows));
		};
	}

	_getGroupRowVisibleLeaves(group) {
		const leafRows = group.getAllLeafRows();

		return leafRows.filter((leafRow) => this._report.isRowVisible(leafRow));
	}

	_expandedGroupRowRenderer(groupRow) {
		return this._report.hasSummaryRow({ groupRow })
			? this._expandedGroupWithSummaryRowRenderer(groupRow)
			: this._expandedGroupWithNoSummaryRowRenderer(groupRow);
	}

	_expandedGroupWithSummaryRowRenderer(groupRow) {
		return [
			this._firstRowOfGroupRowRenderer({
				groupRow,
				dataCells: this._emptyDataColumnsRenderer({
					row: groupRow,
					cssClass: this._getDepthCssClass(groupRow),
				}),
			}),
			...this._childRowsOfGroupRowRenderer(groupRow),
			this._summaryRowOfGroupRowRenderer(groupRow),
		];
	}

	_expandedGroupWithNoSummaryRowRenderer(groupRow) {
		return [
			this._firstRowOfGroupRowRenderer({
				groupRow,
				dataCells: this._dataCellsRenderer({ row: groupRow }),
			}),
			...this._childRowsOfGroupRowRenderer(groupRow),
		];
	}

	_firstRowOfGroupRowRenderer({ groupRow, dataCells }) {
		return (html) =>
			html.tr(
				{ klass: this._rowCssClasses(groupRow), "data-test": "row" },
				this._rowTitleRenderer(groupRow),
				dataCells,
			);
	}

	_childRowsOfGroupRowRenderer(groupRow) {
		return this.visitAll(
			groupRow.getVisibleRows().sort((a, b) => a.compareTo(b)),
		);
	}

	_summaryRowOfGroupRowRenderer(groupRow) {
		return (html) =>
			html.tr(
				{ klass: this._getSummaryRowCssClass({ groupRow }) },
				html.th(
					{ klass: this._getSummaryRowCssClass({ groupRow }) },
					_("Sum {title}", { title: groupRow.getTitle() }),
				),
				this._dataCellsRenderer({
					row: groupRow,
					cssClass: this._getSummaryRowCssClass({ groupRow }),
				}),
			);
	}

	_dataCellsRenderer({ row, cssClass = this._getDepthCssClass(row) }) {
		return this._columns.map((column) =>
			this._dataCellRenderer({
				row,
				column,
				cssClass: [cssClass, column.cssClass()],
				content: this._getCellData({ row, column }),
			}),
		);
	}

	_getCellData({ row, column }) {
		return row.getReport().getFormattedData({ row, column });
	}

	_emptyDataColumnsRenderer({ row, cssClass = "" }) {
		return this._columns.map((column) =>
			this._dataCellRenderer({
				row,
				column,
				cssClass: [cssClass, column.cssClass()],
			}),
		);
	}

	_getSummaryRowCssClass({ groupRow }) {
		return ["summary", this._rowCssClasses(groupRow)];
	}

	_rowCssClasses(row) {
		return [
			"title",
			`data-depth-${row.getVisibilityDepth()}`,
			this._cssClassNameRenderer.visit(row),
			this._rowCssStyle(row),
			{ nested: !row.isGroupRow() && row.getVisibilityDepth() > 1 },
		];
	}

	_rowCssStyle(row) {
		const style = row.getStyle();

		if (!style) {
			return "";
		}

		return ["styled", style];
	}

	_getDepthCssClass(row) {
		return `data-depth-${row.getVisibilityDepth()}`;
	}

	_makeDefaultRowTitleRenderer({
		report,
		itemTitleRenderer,
		dimensionsRenderer,
		isFlat,
		onGroupRowCollapseExpandRequest,
	}) {
		const rowTitleVisitor = new RowTitleRenderer({
			report,
			itemTitleRenderer,
			dimensionsRenderer,
			isFlat,
			onGroupRowCollapseExpandRequest,
		});

		return (row) => rowTitleVisitor.visit(row);
	}
}
